import React from "react";
import { Box, Separator, Tabs, TabsList, TabsPanel, TabsTab } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePresence } from "#app/lib/data/presence-provider";
import { memberStatusBadge, type EntityData } from "#app/lib/entity-visuals";
import { useMemberDetail } from "#app/lib/hooks/use-member-detail";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { AnalyticsContext, MemberEvent, trackEvent } from "#app/lib/analytics";
import { isInvited, isFormerMember } from "#app/lib/role-utils";
import { PRESENCE_META } from "#app/lib/constants/presence-meta";
import {
  MemberRowMenuDialog,
  type ConfirmAction,
} from "#app/components/members/member-row-menu";
import { InvitePendingBanner } from "./member-profile/invite-pending-banner";
import {
  MemberProfileActivitySection,
  MemberProfileActivityTab,
} from "./member-profile/member-profile-activity";
import { MemberProfileFields } from "./member-profile/member-profile-fields";
import { MemberProfileHeader } from "./member-profile/member-profile-header";
import { MemberProfileOrgChartTab } from "./member-profile/member-profile-org-chart-tab";

type MemberProfilePanelProps = Omittable<{
  userId: string | null;
  /** Drives data fetching + close affordance; the island chrome itself is
   * owned by AppShell's `rightPanel` slot. */
  open: boolean;
  onClose: () => void;
  onEditProfile?: (userId: string) => void;
  /** Navigate the island to another member's profile (e.g. clicking the
   * "Reports to {name}" manager link). */
  onViewMember?: (userId: string) => void;
  /** Viewing your own island (self) vs. an admin viewing another member —
   * drives the edit action's label/icon ("Edit profile" vs "Manage member"). */
  isSelf?: boolean;
  /** manage.users — may rename/remove any custom job title from the vocabulary. */
  canManage?: boolean;
  /** Current viewer id — the creator of a custom title may rename/remove it. */
  currentUserId?: string | null;
}>;

/**
 * The profile island body. Renders directly into AppShell's `rightPanel`
 * slot — NOT a Dialog — so it owns its own header, tab strip, and scroll
 * body instead of going through `EntityDetailShell`.
 */
export function MemberProfilePanel({
  userId,
  open,
  onClose,
  onEditProfile,
  onViewMember,
  isSelf = false,
  canManage = false,
  currentUserId = null,
}: MemberProfilePanelProps) {
  const { user, loading } = useMemberDetail(userId ?? undefined, {
    skip: !open,
  });
  const { updateMember } = useMemberMutations();
  const [tab, setTab] = React.useState("overview");
  // The workspace roster already carries this member's identity — name,
  // avatar, title, status, username, email, manager — so the header and those
  // rows paint real on the island's first frame; only the values the roster
  // lacks (phone, teams) wait on the fetch. Their skeletons gate on initial
  // load only: once the member is cached, background refetches never blank
  // them. A member outside the roster keeps the identity skeletons.
  const { userMap } = useWorkspaceData();
  const rosterUser = userId ? userMap.get(userId) : undefined;
  const known = user ?? rosterUser ?? null;
  const initialLoading = loading && !user;
  const identityLoading = initialLoading && !rosterUser;

  const handleTitleChange = React.useCallback(
    (title: string | null) => {
      if (!userId) return;
      void updateMember(userId, { title }).then((result) => {
        if (!result) return;
        trackEvent({
          context: AnalyticsContext.Member,
          event: MemberEvent.ChangeTitle,
          data: { cleared: title == null, self: userId === currentUserId },
        });
      });
    },
    [userId, updateMember, currentUserId],
  );

  const entity: EntityData = {
    kind: "user",
    id: userId ?? "unknown",
    user: known,
  };
  const former = isFormerMember(known);
  const invited = isInvited(known);
  const presence = usePresence(userId);
  // Live presence describes an ACTIVE member. Someone invited, suspended or
  // gone has no session to be online in, so the chip carries their member
  // status instead — "Invited" is the one signal that the person has no
  // account yet.
  const statusChip =
    invited || former
      ? memberStatusBadge(known?.memberStatus)
      : PRESENCE_META[presence?.status ?? "OFFLINE"];

  const [confirmAction, setConfirmAction] =
    React.useState<ConfirmAction | null>(null);
  const handleConfirmed = React.useCallback(
    (action: ConfirmAction) => {
      // The cancelled invitee is gone from the workspace — nothing left to show.
      if (action.type === "cancel-invite") onClose();
    },
    [onClose],
  );

  const canEdit = Boolean(onEditProfile && userId);
  // The reporting line is an org fact with one owner: manage.users. Self-edit
  // covers profile fields only — the server rejects a self managerId change
  // without the permission, so the row's affordances match it.
  const canEditReportsTo = Boolean(canManage && userId);

  return (
    <Box direction="column" fill group="pane" minHeight={0}>
      <MemberProfileHeader
        canEdit={canEdit}
        canManage={canManage}
        currentUserId={currentUserId}
        entity={entity}
        former={former}
        identityLoading={identityLoading}
        isSelf={isSelf}
        known={known}
        onClose={onClose}
        onEditProfile={onEditProfile}
        onTitleChange={handleTitleChange}
        statusChip={statusChip}
        userId={userId}
      />

      <Tabs
        className="min-h-0 grow"
        onValueChange={(v) => {
          setTab(v as string);
          trackEvent({
            context: AnalyticsContext.Member,
            event: MemberEvent.SwitchProfileTab,
            data: { tab: v as string },
          });
        }}
        value={tab}
      >
        {/* Group the tab row + divider so the strip's underline rests on the
            hairline below it. */}
        <Box direction="column" shrink={false}>
          <Box paddingTop="sm" paddingX="md">
            {/* Same strip grammar as the workflow view tabs (neutral
                underline bar), label-only — the panel's fixed anchors carry
                no leading tiles. */}
            <TabsList variant="underline">
              <TabsTab value="overview">Overview</TabsTab>
              <TabsTab value="activity">Activity</TabsTab>
              <TabsTab value="orgchart">Org Chart</TabsTab>
            </TabsList>
          </Box>
          <Separator />
        </Box>

        <TabsPanel className="overflow-y-auto" value="overview">
          <Box direction="column" gap="md" paddingX="md" paddingY="md">
            {invited ? (
              <InvitePendingBanner
                canManage={canManage}
                loading={initialLoading}
                onCancelInvite={(member) =>
                  setConfirmAction({ type: "cancel-invite", member })
                }
                user={user}
                userId={userId}
              />
            ) : null}
            <MemberProfileFields
              canEditReportsTo={canEditReportsTo}
              initialLoading={initialLoading}
              known={known}
              onViewMember={onViewMember}
              user={user}
              userId={userId}
            />

            <Separator />

            <MemberProfileActivitySection userId={userId} />
          </Box>
        </TabsPanel>

        <TabsPanel className="overflow-y-auto" value="activity">
          <MemberProfileActivityTab userId={userId} />
        </TabsPanel>

        <TabsPanel value="orgchart">
          <MemberProfileOrgChartTab
            isSelf={isSelf}
            onClose={onClose}
            onViewMember={onViewMember}
            userId={userId}
          />
        </TabsPanel>
      </Tabs>
      <MemberRowMenuDialog
        action={confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirmed={handleConfirmed}
      />
    </Box>
  );
}
